import { ActiveSearchTags } from '@/components/ActiveSearchTags';
import { CollapsableSearch } from '@/components/CollapsableSearch';
import { useLoginInfo } from '@/components/LoginInfo/provider';
import { useErrorInlineNotificationAPI } from '@/components/ErrorInlineNotification/ErrorInlineNotification';
import { ErrorBoundary } from '@/components/Rollbar';
import { SuspenseLoading } from '@/components/SuspenseErrorBoundary/fallback';
import {
  ACTION_BUTTONS_GAP,
  EMPTY_PAYEE_ARRAY,
  EXCLUDED_ACTIVE_TAG_FIELDS,
  INVOICE_REPORTS_ALWAYS_SHOW_FIELDS,
} from '@/features/invoice_reports/constants';
import type { InvoiceReportsSearchQuery } from '@/features/invoice_reports/types';
import { useShowSearchValue } from '@/hooks/invoice_reports/useShowSearchValue';
import { useTranslation } from '@/i18n';
import type { OfficeInvoiceReportSettings } from 'ap-openapi';
import classnames from 'classnames/bind';
import {
  FC,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { ErrorFallback } from './components/ErrorFallback';
import { SearchForm } from './components/SearchForm';
import {
  InvoiceReportsSearchFormData,
  useAgentApplicantInitialData,
  useAgentApplicantTransformer,
  useApPayeeInitialData,
  useApPayeeTransformer,
  useConfigForm,
  useInvoiceReportsFieldOrders,
  usePaymentStatusTransformer,
  useSearchForm,
} from './hooks';
import styles from './Search.module.scss';

const cx = classnames.bind(styles);

interface SearchProps {
  defaultValues?: InvoiceReportsSearchFormData;
  onSearch: (data: InvoiceReportsSearchQuery) => void;
  searchQuery?: InvoiceReportsSearchQuery;
  isLoading?: boolean;
  officeInvoiceReportSettings?: OfficeInvoiceReportSettings;
}

export const InnerSearch: FC<SearchProps> = memo(
  ({
    defaultValues,
    onSearch,
    searchQuery,
    isLoading,
    officeInvoiceReportSettings,
  }) => {
    const { t } = useTranslation();
    const form = useForm<InvoiceReportsSearchFormData>({
      defaultValues,
      mode: 'all',
    });
    const canAgentEntry =
      officeInvoiceReportSettings?.office_member_setting?.can_agent_entry ??
      false;

    const payeeIds = useMemo(
      () => defaultValues?.ap_payee_ids ?? EMPTY_PAYEE_ARRAY,
      [defaultValues?.ap_payee_ids]
    );

    const agentApplicantIds = useMemo(
      () => defaultValues?.agent_applicant_office_member_ids ?? [],
      [defaultValues?.agent_applicant_office_member_ids]
    );

    const { options: payeeOptions, isLoaded: payeeOptionsLoaded } =
      useApPayeeInitialData(payeeIds);
    const {
      options: agentApplicantOptions,
      isLoaded: agentApplicantOptionsLoaded,
    } = useAgentApplicantInitialData(agentApplicantIds);

    // Remove stale proxy applicant IDs (removed from the system) from the form
    // value after initial data confirms which IDs still exist.
    // Mirrors the ExItemSelect pattern: run once when loaded, check value against
    // resolved options, clear any IDs that were not returned by the API.
    const agentApplicantOptionsLoadedRef = useRef(false);
    const selectedAgentApplicantIds = useWatch({
      name: 'agent_applicant_office_member_ids',
      control: form.control,
    });
    useEffect(() => {
      if (
        !agentApplicantOptionsLoaded ||
        agentApplicantOptionsLoadedRef.current
      )
        return;
      agentApplicantOptionsLoadedRef.current = true;

      const ids = Array.isArray(selectedAgentApplicantIds)
        ? selectedAgentApplicantIds
        : [];
      if (!ids.length) return;

      const validIds = ids.filter((id) =>
        agentApplicantOptions.some((opt) => String(opt.value) === id)
      );
      if (validIds.length < ids.length) {
        form.setValue('agent_applicant_office_member_ids', validIds, {
          shouldValidate: true,
        });
      }
    }, [
      agentApplicantOptionsLoaded,
      agentApplicantOptions,
      selectedAgentApplicantIds,
      form,
    ]);

    const { customProps, checkLengthValidationRules } = useConfigForm();
    const { currentOfficeMember } = useLoginInfo();
    const { handleReset } = useSearchForm({ form });
    const setError = useErrorInlineNotificationAPI();

    // Use primitive dependencies for sort fields
    const sortFields = defaultValues?.sort_fields;
    const sortOrders = defaultValues?.sort_orders;

    const onHandleSearch = useCallback(
      (data: InvoiceReportsSearchFormData) => {
        const {
          status,
          mf_file,
          payment_status,
          agent_applicant_office_member_ids,
          ...rest
        } = data;

        if (canAgentEntry && !agent_applicant_office_member_ids?.length) {
          setError(
            new Error(
              t('invoice_reports.search.agent_applicant_office_member.required')
            )
          );
          return;
        }

        onSearch({
          ...rest,
          status: status === '' || status === null ? undefined : status,
          mf_file: mf_file === '' || mf_file === null ? undefined : mf_file,
          payment_status:
            payment_status === '' || payment_status === null
              ? undefined
              : payment_status,
          ...(canAgentEntry &&
            agent_applicant_office_member_ids?.length && {
              agent_applicant_office_member_ids,
            }),
          sort_fields: sortFields,
          sort_orders: sortOrders,
        });
      },
      [onSearch, sortFields, sortOrders, canAgentEntry, setError, t]
    );

    const payeeOptionsLoadedRef = useRef(false);
    const selectedPayeeIds = useWatch({
      name: 'ap_payee_ids',
      control: form.control,
    });
    useEffect(() => {
      if (!payeeOptionsLoaded || payeeOptionsLoadedRef.current) return;
      payeeOptionsLoadedRef.current = true;

      const ids = Array.isArray(selectedPayeeIds) ? selectedPayeeIds : [];
      if (!ids.length) return;

      const validIds = ids.filter((id) =>
        payeeOptions.some((opt) => String(opt.value) === id)
      );
      if (validIds.length < ids.length) {
        form.setValue('ap_payee_ids', validIds, { shouldValidate: true });
      }
    }, [payeeOptionsLoaded, payeeOptions, selectedPayeeIds, form]);

    /** Exclude agent tag when canAgentEntry is false  */
    const excludeFields = useMemo(() => {
      const fields = [...EXCLUDED_ACTIVE_TAG_FIELDS];
      if (!canAgentEntry) {
        fields.push('agent_applicant_office_member_ids');
      }
      return fields;
    }, [canAgentEntry]);

    const { FIELD_ORDERS } = useInvoiceReportsFieldOrders();
    const paymentStatusTransformers = usePaymentStatusTransformer();
    const agentApplicantTransformers = useAgentApplicantTransformer(
      agentApplicantOptions
    );
    const apPayeeTransformers = useApPayeeTransformer(
      payeeOptions,
      officeInvoiceReportSettings
    );
    const customTransformers = useMemo(
      () => ({
        ...paymentStatusTransformers,
        ...agentApplicantTransformers,
        ...apPayeeTransformers,
      }),
      [
        paymentStatusTransformers,
        agentApplicantTransformers,
        apPayeeTransformers,
      ]
    );

    // Initialize to 220 so --action-buttons-space = 220 + 8 = 228px on first render,
    // matching the CSS fallback and preventing the section title from overlapping action buttons.
    const [actionButtonsWidth, setActionButtonsWidth] = useState(220);
    const headerStyle = useMemo<Record<string, string>>(
      () => ({
        '--action-buttons-space': `${
          actionButtonsWidth + ACTION_BUTTONS_GAP
        }px`,
      }),
      [actionButtonsWidth]
    );

    const { searchValuesShow } = useShowSearchValue({
      searchQuery,
      fieldOrders: FIELD_ORDERS,
      payeeOptions: payeeOptions,
      excludeFields,
      customTransformers,
      alwaysShowFields: INVOICE_REPORTS_ALWAYS_SHOW_FIELDS,
    });

    // Memoize the render callback for CollapsableSearch
    const renderSearchForm = useCallback(
      () => (
        <SearchForm
          agentApplicantDefaultOptions={agentApplicantOptions}
          appPayeeDefaultOptions={payeeOptions}
          canAgentEntry={canAgentEntry}
          customProps={customProps}
          checkLengthValidationRules={checkLengthValidationRules}
          form={form}
          onSubmit={onHandleSearch}
          onReset={handleReset(canAgentEntry, currentOfficeMember?.id)}
          isLoading={isLoading}
          onActionButtonsWidthChange={setActionButtonsWidth}
          officeInvoiceReportSettings={officeInvoiceReportSettings}
        />
      ),
      [
        agentApplicantOptions,
        payeeOptions,
        canAgentEntry,
        currentOfficeMember?.id,
        customProps,
        checkLengthValidationRules,
        form,
        onHandleSearch,
        handleReset,
        isLoading,
        setActionButtonsWidth,
        officeInvoiceReportSettings,
      ]
    );

    return (
      <div className={cx('search-container')}>
        <div className={cx('header')} style={headerStyle}>
          <CollapsableSearch
            initialOpen
            openTitle={t('invoice_reports.search.collapse_title')}
            closeTitle={t('invoice_reports.search.expand_title')}
            sectionTitleExpandedClassName={cx('section-title-expanded')}
          >
            {renderSearchForm}
          </CollapsableSearch>
        </div>
        <ActiveSearchTags activeSearchValues={searchValuesShow} />
      </div>
    );
  }
);
InnerSearch.displayName = 'InnerSearch';

// Memoized error fallback render function
const renderErrorFallback = ({ error }: { error: Error | null }) => (
  <ErrorFallback error={error} />
);

export const Search: FC<SearchProps> = memo(
  ({
    defaultValues,
    onSearch,
    searchQuery,
    isLoading,
    officeInvoiceReportSettings,
  }) => {
    return (
      <Suspense
        fallback={
          <SuspenseLoading className={cx('search-container-loading')} />
        }
      >
        <ErrorBoundary fallback={renderErrorFallback}>
          <InnerSearch
            defaultValues={defaultValues}
            onSearch={onSearch}
            searchQuery={searchQuery}
            isLoading={isLoading}
            officeInvoiceReportSettings={officeInvoiceReportSettings}
          />
        </ErrorBoundary>
      </Suspense>
    );
  }
);
Search.displayName = 'Search';
